import { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { teamsApi } from '../services/api';
import { formatDate } from '../utils/dateFormat';


export default function Teams() {
  const navigate = useNavigate();
  const { selectedPortal } = useHubSpotPortals();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [teamsQueried, setTeamsQueried] = useState(false);
  // Add Team Modal state
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const newTeamRoutingRule = 1;
   console.log('DEBUG: Teams component mounted, selectedPortal =', selectedPortal);

  useEffect(() => {
    if(teamsQueried && teams.length > 0){
      setLoading(false)
    }
    else if (teamsQueried && teams.length <= 0){
      setLoading(true);
    }
  },[teamsQueried, teams.length])

  // the above deps were added since github was saying theyre required

  // ✅ Fetch teams based on active portal ID
  const fetchTeams = useCallback(async () => {
    console.log('fetching teams');
    if (!selectedPortal?.portal_id) {
      setError('No active portal selected.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await teamsApi.fetchTeams(selectedPortal.portal_id); // ✅ use correct property
      console.log('teams data', data);
      const filteredTeams = (data.teams || []).filter(team => team.custom_team_name);
      if (filteredTeams.length > 0){
        setTeams(filteredTeams);
      }
      else{
        setTeams([])
      }
      setTeamsQueried(true);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams. Please try again.');
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [selectedPortal?.portal_id]);

  // ✅ Trigger fetch when the active portal changes
  useEffect(() => {
    if (selectedPortal?.portal_id) {
      setHasFetched(false);
      fetchTeams();
    }
  }, [selectedPortal, fetchTeams]);

  const handleEditTeam = (teamId) => {
    navigate(`/teams/${teamId}`);
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    const confirmMessage = `Are you sure you want to delete the team "${teamName}"?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await teamsApi.deleteTeam(teamId);
    } catch (err) {
      console.error('Error deleting team:', err);
      alert('Failed to delete team. Please try again.');
      return;
    }

    // The deletion succeeded, so update the UI before refreshing from the API.
    setTeams(currentTeams => currentTeams.filter(team => team.id !== teamId));

    // Refresh separately so it can never turn a successful delete into a delete error.
    await fetchTeams();
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      alert('Please enter a team name');
      return;
    }

    if (!selectedPortal?.portal_id) {
      alert('No active portal selected');
      return;
    }

    console.log('📤 Team payload:', {
      hs_portal_id: selectedPortal.portal_id,
      custom_team_name: newTeamName,
      member_routing_rule: newTeamRoutingRule,
    });

    try {
      const created = await teamsApi.createTeam({
        hs_portal_id: selectedPortal.portal_id,
        custom_team_name: newTeamName,
        member_routing_rule: newTeamRoutingRule,
      });

      // Add the new team to the list
      setTeams([...teams, created.team]);
      setShowAddTeamModal(false);
      setNewTeamName('');
    } catch (err) {
      console.error('❌ Error creating team:', err);
      alert('Failed to create team');
    }
  };

  const visibleTeams = includeInactive
    ? teams
    : teams.filter((team) => team.team_active);
  const hasTeams = visibleTeams.length > 0;
  const showLoading = loading || (!!selectedPortal?.portal_id && !hasFetched);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Teams</h1>
            {/* Show portal info if available */}
{selectedPortal && (
  <p className="text-sm text-blue-700 mt-1">
    Showing teams for portal: 
    <span className="font-semibold"> {selectedPortal.hub_domain}</span> 
    <span className="ml-1 text-gray-600">(ID: {selectedPortal.portal_id})</span>
  </p>
)}
          </div>
          <button
            onClick={() => setShowAddTeamModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center space-x-2"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Team</span>
          </button>
        </div>
        <div className="mt-4 flex items-center justify-end">
          <label className="inline-flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              className="mr-2"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            Show inactive teams
          </label>
        </div>
      </div>

      {/* Teams Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {showLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading teams...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-8 mb-4">
              <p className="text-red-800">{error}</p>
              <button 
                onClick={fetchTeams}
                className="mt-2 text-red-600 hover:text-red-700 text-sm underline"
              >
                Retry
              </button>
            </div>
          </div>
        ) : hasTeams ? (
          /* Teams Table */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Object Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Weight
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleTeams.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {team.custom_team_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                      {team.object_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(team.date_created)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(team.last_updated)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        team.team_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {team.team_active ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {team.team_weight ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditTeam(team.id)}
                          className="text-blue-600 hover:text-blue-800 transition duration-200"
                          title="Edit team"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(team.id, team.custom_team_name)}
                          className="text-red-600 hover:text-red-800 transition duration-200"
                          title="Delete team"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-12">
            {teamsQueried && teams.length <= 0 ? <>
              <FiUsers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No teams yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                You haven't created any teams yet. Start by creating your first team to organize your members.
              </p>
              <button
                  onClick={() => setShowAddTeamModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 inline-flex items-center space-x-2"
              >
                <FiPlus className="w-4 h-4" />
                <span>Create Your First Team</span>
              </button>
            </> : null}

          </div>
        )}
      </div>

      {/* Add Team Modal */}
      {showAddTeamModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Add New Team</h2>

            <label className="block mb-2 text-sm">Team Name</label>
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-4"
              placeholder="Enter team name"
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowAddTeamModal(false);
                  setNewTeamName('');
                }}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTeam}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Team
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
